import { DeviceDTO, DeviceShadowDTO } from '@eppendorf/vnls-device-service-utils';
import {
  Button,
  Icon,
  IconSizeClasses,
  SortableTable,
} from '@eppendorf/vnls-react-components';
import { createColumnHelper } from '@tanstack/react-table';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError, useSearchParams } from 'react-router-dom';

import { Pagination as PaginationType } from '$shared/types';

import { ActivityStatus } from '$components/device/activity-status/activity-status';
import { DeviceNameCell } from '$components/device/name-cell';
import { LoadingErrorHint } from '$components/loading-error-hint/loading-error-hint';
import { Overview } from '$components/overview-box/overview-box';
import { SearchInput } from '$components/search-input/search-input';
import { Pagination } from '$components/table/pagination/pagination';
import { DEVICES_WITHOUT_PARAMETERS } from '$features/monitoring/shared/devices-without-parameters';
import { useDevicesV2WithQueryParams } from '$features/monitoring/v2/devicesv2.api';
import { DoorOrLidStatusV2 } from '$features/monitoring/v2/door-or-lid-statusV2';
import { MonitoringCreateReportAction } from '$features/monitoring/v2/monitoring-create-report-action';
import { UnitAlertStateV2 } from '$features/monitoring/v2/unit-alert-stateV2';
import { UnlessOfflineV2 } from '$features/monitoring/v2/unless-offlineV2';
import { isValueWithKeyInSearchParams } from '$features/navigation/isValueWithKeyInSearchParams';

import './monitoring-list.scss';

export function MonitoringList(): ReactElement {
  const [{ page, pageSize }, setPagination] = useState<PaginationType>({
    page: 1,
    pageSize: 10,
  });

  const [searchQuery, setSearchQuery] = useState<string>('');

  const handleSearchQueryChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setPagination({ page: 1, pageSize });
    },
    [pageSize],
  );

  const {
    data: datav2,
    isError,
    isLoading,
  } = useDevicesV2WithQueryParams({
    page,
    pageSize,
    searchQuery,
    connectionStatus: 'paired',
  });

  const routeError = useRouteError();

  const { t } = useTranslation();
  const navigate = useNavigate();

  function handleRowClick(deviceV2: DeviceDTO) {
    if (!deviceV2) return;
    const hasParameters = !DEVICES_WITHOUT_PARAMETERS.includes(deviceV2.model ?? '');
    const tab = hasParameters ? 'parameters' : 'events';
    navigate({
      search: `?sidecarRoute=monitoring/${tab}&manufacturer=${deviceV2.manufacturer}&serialNumber=${deviceV2.serialNumber}`,
    });
  }

  const columnHelper = createColumnHelper<DeviceDTO & DeviceShadowDTO>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('activityState', {
        header: () => t('monitoring.activityStatus'),
        id: 'activityStatus',
        meta: { dataLabel: t('monitoring.activityStatus') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted
        cell: (info) => <ActivityStatus activityStatus={info.getValue() || null} />,
      }),
      columnHelper.accessor('name', {
        header: () => t('monitoring.device'),
        id: 'device',
        meta: { dataLabel: t('monitoring.device') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted until we figure out a better way
        cell: ({ row: { original: device } }) => (
          <DeviceNameCell device={device} className="monitoring-list__device-name-cell" />
        ),
      }),
      columnHelper.accessor('aggregateAlertState', {
        header: () => t('monitoring.alertStatus'),
        id: 'alertStatus',
        meta: { dataLabel: t('monitoring.alertStatus') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted until we figure out a better way
        cell: (info) => (
          <div className="w-max-xxs flex flex__jc--center">
            <UnlessOfflineV2 unitState={info.row.original.activityState}>
              <UnitAlertStateV2 alertState={info.getValue()} side="top" />
            </UnlessOfflineV2>
          </div>
        ),
      }),
      columnHelper.accessor('doorState', {
        header: () => t('monitoring.doorOrLid'),
        id: 'doorLidStatus',
        meta: { dataLabel: t('monitoring.doorOrLid') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted until we figure out a better way
        cell: (info) => (
          <div className="w-max-xxs flex flex__jc--center">
            <UnlessOfflineV2 unitState={info.row.original.activityState}>
              <DoorOrLidStatusV2
                doorState={info.getValue()}
                lidState={info.row.original.lidState}
                fallback="-"
              />
            </UnlessOfflineV2>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => t('monitoring.actions'),
        meta: { dataLabel: t('monitoring.actions') },
        // eslint-disable-next-line react/no-unstable-nested-components -- accepted until we figure out a better way
        cell: ({ row: { original: device } }) => (
          <MonitoringCreateReportAction device={device} />
        ),
      }),
    ],
    [],
  );

  const handlePageSizeChange = (newPageSize: string) => {
    setPagination({ page: 1, pageSize: Number(newPageSize) });
  };

  const handlePageChange = (newPage: number) => {
    setPagination({ pageSize, page: newPage });
  };

  const [searchParams] = useSearchParams();

  return (
    <>
      <Overview.Header>
        <div className="monitoring-list__header">
          <h1 className="monitoring-list__title title">{t('monitoring.overview')}</h1>
          <SearchInput
            onSearchQueryChange={handleSearchQueryChange}
            placeholder={t('devices.searchPlaceholder')}
            id="monitoring-overview-search"
            className="monitoring-list__search-wrapper"
          />
        </div>
      </Overview.Header>
      <Overview.Body>
        {(routeError || isError) && <LoadingErrorHint />}

        {!isLoading && !isError && (
          <>
            {datav2 && (datav2?.data?.length ?? 0) > 0 && (
              <>
                <SortableTable<
                  (DeviceDTO & DeviceShadowDTO)[],
                  DeviceDTO & DeviceShadowDTO
                >
                  data={datav2.data || []}
                  columns={columns}
                  onRowClick={(device) => handleRowClick(device)}
                  isSelectedRow={(device) =>
                    isValueWithKeyInSearchParams(
                      'serialNumber',
                      device?.serialNumber,
                      searchParams,
                    )
                  }
                  isTableToCardDesign
                  className="monitoring-list__sortable-table"
                />
                {datav2.pagination && (
                  <Pagination
                    paginationData={datav2.pagination}
                    onPageChange={(newPage) => handlePageChange(newPage)}
                    onPageSizeChange={(newSize) => handlePageSizeChange(newSize)}
                    pageSizeOptions={[10, 25, 50, 100]}
                  />
                )}
              </>
            )}

            {datav2?.data?.length === 0 && !searchQuery && (
              <div className="box flex flex__dir--column flex__ai--center p-xxxl">
                <Icon
                  name="not-paired"
                  size={IconSizeClasses.Large}
                  className="bg-gray-500"
                />
                <h1 className="font-weight-medium font-size-xl p-xl">
                  {t('monitoring.emptyState')}
                </h1>
                <p className="m-bottom-xxl">{t('monitoring.emptyStateHint')}</p>
                <Button
                  size="medium"
                  onClick={() => {
                    navigate('/devices');
                  }}
                >
                  {t('monitoring.gotoDeviceOverview')}
                </Button>
              </div>
            )}
            {datav2?.data?.length === 0 && searchQuery && (
              <div className="box flex flex__dir--column flex__ai--center p-xxxl">
                <Icon
                  name="no-results"
                  size={IconSizeClasses.Large}
                  className="bg-gray-500"
                />
                <h1 className="font-weight-medium font-size-xl p-xl">
                  {t('devices.emptySearch')}
                </h1>
                <p className="m-bottom-xxl w-max-xl text-align-center">
                  {t('shared.tryAdjustSearchCriteria')}
                </p>
              </div>
            )}
          </>
        )}
      </Overview.Body>
    </>
  );
}
